import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Spinner,
  Icon,
  Badge,
  Divider,
} from '@chakra-ui/react';
import {
  FiCheckCircle,
  FiAlertCircle,
  FiFileText,
  FiPackage,
  FiHome,
  FiShield,
} from 'react-icons/fi';
import { apiUrl } from '../ApiUrl/ApiUrl';

const PaymentCallback = () => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get("orderId");
    const oid = localStorage.getItem("order_id") || "";

    fetch(`${apiUrl}/public/api/payment/getByOid?orderId=${orderId}&oid=${oid}`, {
      method: "GET",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to verify payment status.");
        }
        return response.json();
      })
      .then((data) => {
        setPaymentDetails(data);
      })
      .catch((err) => {
        console.error("Error:", err);
        setError("Could not retrieve payment details. If your payment was deducted, please check your Orders page or contact our chambers.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return (
    <Box bg="#F8FAFC" minH="85vh" py={{ base: 10, md: 16 }} display="flex" alignItems="center">
      <Container maxW="lg" px={4}>
        <Box
          bg="white"
          borderRadius="2xl"
          p={{ base: 6, md: 8 }}
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.100"
          textAlign="center"
        >
          {loading ? (
            <VStack spacing={6} py={8}>
              <Spinner size="xl" thickness="4px" speed="0.75s" color="#162237" emptyColor="gray.100" />
              <VStack spacing={2}>
                <Heading size="md" color="#162237">
                  Verifying Payment Settlement...
                </Heading>
                <Text fontSize="sm" color="gray.500" maxW="sm">
                  Please hold while our judicial gateway validates your transaction and provisions your tax invoice.
                </Text>
              </VStack>
            </VStack>
          ) : error ? (
            <VStack spacing={6} py={4}>
              <Box
                w="64px"
                h="64px"
                borderRadius="full"
                bg="red.50"
                color="red.500"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Icon as={FiAlertCircle} boxSize={8} />
              </Box>
              <VStack spacing={2}>
                <Heading size="md" color="#162237">
                  Verification Notice
                </Heading>
                <Text fontSize="sm" color="gray.600">
                  {error}
                </Text>
              </VStack>
              <HStack spacing={4} w="full" pt={2}>
                <Button as={Link} to="/orderpage" flex={1} bg="#162237" color="white" _hover={{ bg: '#0B132B' }}>
                  View My Orders
                </Button>
                <Button as={Link} to="/" flex={1} variant="outline" borderColor="gray.200">
                  Go to Home
                </Button>
              </HStack>
            </VStack>
          ) : (
            paymentDetails && (
              <VStack spacing={6} align="stretch">
                <Box textAlign="center">
                  <Box
                    w="72px"
                    h="72px"
                    mx="auto"
                    mb={4}
                    borderRadius="full"
                    bg="emerald.50"
                    color="#059669"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow="0 0 0 10px rgba(16, 185, 129, 0.08)"
                  >
                    <Icon as={FiCheckCircle} boxSize={9} />
                  </Box>
                  <Badge
                    bg="rgba(16, 185, 129, 0.1)"
                    color="#059669"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="xs"
                    fontWeight="bold"
                    textTransform="uppercase"
                    mb={2}
                  >
                    Verified Transaction
                  </Badge>
                  <Heading size="lg" color="#162237">
                    Payment Successful!
                  </Heading>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Your judicial order has been secured and confirmed.
                  </Text>
                </Box>

                {/* Details Breakdown */}
                <Box bg="#F8FAFC" p={5} borderRadius="xl" border="1px solid" borderColor="gray.200">
                  <VStack spacing={3} align="stretch" fontSize="sm">
                    <HStack justify="space-between">
                      <Text color="gray.500">Order Reference:</Text>
                      <Text fontWeight="bold" color="#162237" fontFamily="monospace">
                        {paymentDetails.oid || paymentDetails.receiptId}
                      </Text>
                    </HStack>
                    {paymentDetails.payid && (
                      <HStack justify="space-between">
                        <Text color="gray.500">Transaction ID:</Text>
                        <Text fontWeight="medium" color="gray.700" fontFamily="monospace">
                          {paymentDetails.payid}
                        </Text>
                      </HStack>
                    )}
                    <HStack justify="space-between">
                      <Text color="gray.500">Status:</Text>
                      <Badge colorScheme="green" variant="subtle" px={2} py={0.5} borderRadius="md">
                        {paymentDetails.status || 'CONFIRMED'}
                      </Badge>
                    </HStack>
                    <Divider borderColor="gray.200" />
                    <HStack justify="space-between">
                      <Text fontWeight="semibold" color="#162237">
                        Amount Paid:
                      </Text>
                      <Text fontWeight="extrabold" color="#162237" fontSize="md">
                        ₹{(paymentDetails.amount / 100).toFixed(2)}
                      </Text>
                    </HStack>
                  </VStack>
                </Box>

                {/* Navigation Actions */}
                <VStack spacing={3} align="stretch">
                  <Button
                    as={Link}
                    to={`/invoice?orderId=${paymentDetails.oid || paymentDetails.receiptId}`}
                    size="lg"
                    bg="#162237"
                    color="white"
                    leftIcon={<FiFileText />}
                    _hover={{ bg: '#0B132B' }}
                  >
                    View & Download Tax Invoice
                  </Button>
                  <HStack spacing={3}>
                    <Button
                      as={Link}
                      to="/orderpage"
                      flex={1}
                      variant="outline"
                      borderColor="gray.200"
                      leftIcon={<FiPackage />}
                      _hover={{ bg: 'gray.50' }}
                    >
                      My Orders
                    </Button>
                    <Button
                      as={Link}
                      to="/"
                      flex={1}
                      variant="outline"
                      borderColor="gray.200"
                      leftIcon={<FiHome />}
                      _hover={{ bg: 'gray.50' }}
                    >
                      Store Home
                    </Button>
                  </HStack>
                </VStack>
              </VStack>
            )
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default PaymentCallback;
